/** @jsxImportSource @emotion/react */
import {Heading} from "../components/Uikit/Heading";
import {useSelector} from "react-redux";
import {selectProducts, selectProductStatus} from "../reducks/products/selectors";
import {useEffect, useState} from "react";
import {fetchProducts} from "../reducks/products/operations";
import {Product} from "../reducks/products/types";
import {arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {selectCategories, selectCategoryStatus} from "../reducks/categories/selectors";
import {fetchCategories} from "../reducks/categories/thunks";
import {Category} from "../reducks/categories/types";
import {Accordion} from "../components/Uikit/Accordion";
import {Sortable, SortListItem} from "../components/Uikit/Sortable";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {insertBetween} from "../utils/arrayUtils";
import {IconButton} from "@mui/material";
import Button from "@material-ui/core/Button";
import {CircularProgress} from "@material-ui/core";
import toast from "react-hot-toast";
import {Hr} from "../components/Uikit/Hr";
import {updateProductSortOrder} from "../reducks/products/thunks";
import {useAppThunkDispatch} from "../reducks/store/store";

const categorySortId = 'category-context';
const productSortId = (categoryId: string) => `${categoryId}-product-context`
const recommendedProductSortId = 'product-recommended-context' // product を先に書いて万が一 productSortId と重複するのを回避

// Sortable の中に
const SortableProduct = ({product, onStarPressed}: { product: Product, onStarPressed: (product: Product) => void }) => {
  return (
    <Sortable id={product.id}>
      <SortListItem label={product.name} itemType={'product'}
                    endAdornment={(
                      <>
                        <img alt={`${product.id}-icon`}
                             src={product.images.length > 0 ? product.images[0].path : '/img/no_image.png'}
                             className={'rounded-md w-12 h-12 object-cover'}/>
                        <IconButton onClick={() => onStarPressed(product)}>
                          {product.is_recommended ? <StarRoundedIcon className={'text-yellow-400'}/> :
                            <StarOutlineRoundedIcon/>}
                        </IconButton>
                      </>
                    )}/>
    </Sortable>
  )
}

type ProductMapType = { [key: string]: Product[] };

export default function SortingPage() {
  // カテゴリーIDとそれに属するProductのMap(Object)
  const [categoryToProducts, setCategoryToProducts] = useState<ProductMapType>({});
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [edited, setEdited] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {distance: 5}
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // ドラッグ&ドロップされたときに配列の並び替えを行う
  const handleArrayDragEnd = <T extends { id: string
  }>(event: DragEndEvent, items: Array<T>, setItems: (arr: Array<T>) => void) => {
    const {active, over} = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((v) => v.id === active.id);
      const newIndex = items.findIndex((v) => v.id === over.id);
      setItems(arrayMove(items, oldIndex, newIndex));
      setEdited(true);
    }
  };

  // おすすめを操作した後，おすすめ商品とカテゴリー内の商品の順番が異なっていたとき，カテゴリー内の順番をおすすめの順番に合わせる
  const fixProductOrderInCategories = (recommendProducts: Product[]) => {
    setCategoryToProducts(prev => {
      for (const category of categories) {
        const cid = category.id;
        prev[cid] = [...recommendProducts.filter(p => p.category === cid), ...prev[cid].filter(p => !p.is_recommended)];
      }
      return prev;
    });
  }

  // カテゴリーorカテゴリー内の商品の順番を操作した後，おすすめ商品と順番が異なっていたとき，おすすめの順番をカテゴリーの順番に合わせる
  useEffect(() => {
    setRecommendedProducts(categories.map(ctgr => categoryToProducts[ctgr.id])
      .flat().filter(p => p.is_recommended));
  }, [categories, categoryToProducts]);

  // おすすめボタンを押したとき
  const handleStarPressed = (product: Product) => {
    setCategoryToProducts(prev => {
      const category = product.category;
      const products = prev[category];
      const index = products.findIndex(p => p.id === product.id);
      products[index] = {...product, is_recommended: !(product?.is_recommended ?? false)};
      return {...prev, [category]: products};
    });

    if (product.is_recommended) {
      // おすすめから削除
      setRecommendedProducts(prev => prev.filter(p => p.id !== product.id));
    } else {
      // おすすめに追加
      setRecommendedProducts(prev => [...prev, {...product, is_recommended: true}]);
    }
    setEdited(true);
  }

  const dispatch = useAppThunkDispatch();

  // 保存ボタンを押したとき
  const handleSaveSortOrder = async () => {
    // TODO: Reduxに移す
    setIsSaving(true);

    try {
      await dispatch(updateProductSortOrder({categories, recommendedProducts, cateToProd: categoryToProducts})).unwrap();
      toast.success("保存しました！");
    } catch (e) {
      toast.error("保存に失敗しました");
    }

    setIsSaving(false);
    setEdited(false);
  }

  const productStatus = useSelector(selectProductStatus);
  const _products = useSelector(selectProducts);
  const categoryStatus = useSelector(selectCategoryStatus);
  const _categories = useSelector(selectCategories);

  useEffect(() => {
    if (productStatus === 'idle') dispatch(fetchProducts());
  }, [dispatch, productStatus]);
  useEffect(() => {
    if (categoryStatus === 'idle') dispatch(fetchCategories());
  }, [dispatch, categoryStatus]);
  useEffect(() => {
    if (productStatus === 'succeeded') {
      setCategoryToProducts(_products.reduce((prev, curr) => {
        if (!prev.hasOwnProperty(curr.category)) {
          prev[curr.category] = [curr];
        } else {
          prev[curr.category] = [...prev[curr.category], curr];
        }
        return prev;
      }, {} as ProductMapType));
      setRecommendedProducts(_products.filter(p => p.is_recommended));
    }
  }, [productStatus, _products])
  useEffect(() => {
    if (categoryStatus === 'succeeded') {
      setCategories(_categories);
    }
  }, [categoryStatus, _categories])

  return (
    <div className={'px-4 flex flex-col gap-2 mt-4'}>
      <div className={'flex flex-row items-center justify-between'}>
        <Heading text={"商品の表示順を設定してください"}/>
        <Button color={'primary'} variant={'contained'}
          // FIXME: アニメーションが動かない
                className={'transition-transform transform'}
                disabled={isSaving || !edited} onClick={handleSaveSortOrder}>
          {isSaving && <CircularProgress size={'0.9rem'} className={"mr-2"}/>}
          保存
        </Button>
      </div>
      <Accordion summary={<p className={'text-material-primary ml-2 my-2 w-full'}>おすすめ ({recommendedProducts.length})</p>}
                 endAdornment={<ExpandMoreRoundedIcon color={'primary'}/>}>
        {/* おすすめ商品の並び替え */}
        <DndContext
          id={recommendedProductSortId}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => handleArrayDragEnd(e, recommendedProducts, (items) => {
            setRecommendedProducts(items);
            fixProductOrderInCategories(items);
          })}
        >
          <SortableContext id={recommendedProductSortId} items={recommendedProducts}
                           strategy={verticalListSortingStrategy}>
            {recommendedProducts.map(product => (
              <SortableProduct key={`recommended-${product.id}`} product={product}
                               onStarPressed={handleStarPressed}/>
            ))}
          </SortableContext>
        </DndContext>
      </Accordion>
      <Hr/>
      {/* カテゴリーの並び替え */}
      <DndContext
        id={categorySortId}
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => handleArrayDragEnd(e, categories, setCategories)}
      >
        <SortableContext id={categorySortId} items={categories} strategy={verticalListSortingStrategy}>
          {insertBetween(categories.map((category) => (
            <Sortable key={`${category.id}-sortable`} id={category.id}>
              <Accordion summary={<SortListItem label={category.name} itemType={'category'}/>}
                         endAdornment={<ExpandMoreRoundedIcon color={'primary'}/>}>
                {/* カテゴリー内の商品の並び替え */}
                <DndContext
                  id={productSortId(category.id)}
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) =>
                    handleArrayDragEnd(
                      e,
                      categoryToProducts[category.id],
                      (products) => setCategoryToProducts(prev => ({...prev, [category.id]: products}))
                    )}>
                  <SortableContext id={productSortId(category.id)} items={categoryToProducts[category.id]}
                                   strategy={verticalListSortingStrategy}>
                    {categoryToProducts[category.id].map((product) => (
                      <SortableProduct key={`${product.id}-sortable`} product={product}
                                       onStarPressed={handleStarPressed}/>
                    ))}
                  </SortableContext>
                </DndContext>
              </Accordion>
            </Sortable>
          )), (index) => <Hr key={`hr-${index}`}/>)}
        </SortableContext>
      </DndContext>
    </div>
  );
}
